import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { MapPin, Users, GraduationCap, Mail, Phone, User, Calendar as CalendarIcon, Upload, BookOpen, Building, X, Loader2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar } from '../components/ui/calendar';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ExploreSchools = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('browse'); // 'browse' or 'apply'
  const [approvedGuides, setApprovedGuides] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStep, setFormStep] = useState(1); // Multi-step form: 1, 2, 3, 4
  
  // Schools state - fetched from API
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
    bio: '',
    tourDescription: '',
    // New fields for SchoolScoops-style profile
    gender: '',
    academic_year: '',
    age: '',
    admission_type: '',
    personality_type: '',
    college_rating: '',
    hometown: '',
    major: '',
    minor: '',
    clubs: '',
    college_experience: '',
    tip_for_students: '',
    favorite_class: '',
    career_goals: '',
    ideal_free_night: '',
    high_school: '',
    meeting_point: ''
  });
  
  const [extracurriculars, setExtracurriculars] = useState([]);
  const [housingExperience, setHousingExperience] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [dateTimeMap, setDateTimeMap] = useState({}); // {dateString: [times]}
  const [currentEditingDate, setCurrentEditingDate] = useState(null);
  
  // Options for dropdowns and checkboxes
  const genderOptions = ['Male', 'Female', 'Non-binary', 'Prefer not to say'];
  const academicYearOptions = ['Freshman', 'Sophomore', 'Junior', 'Senior', 'Graduate Student'];
  const admissionTypeOptions = ['Admitted as a freshman', 'Transfer student', 'Graduate admission'];
  const personalityOptions = ['Introverted', 'Somewhat introverted', 'Somewhat extroverted', 'Extroverted'];
  const collegeRatingOptions = ['Excellent', 'Good', 'Average', 'Below average'];
  const extracurricularOptions = [
    'Greek life', 'Club/Organization', 'Job/Internship', 'ROTC', 
    'NCAA varsity sport', 'Recreational sport', 'Student government', 
    'Community service', 'Religious/Cultural group', 'Art/Music/Performance', 
    'Study abroad', 'Research', 'Other'
  ];
  const housingOptions = ['Dorm', 'Off-campus house or apartment', 'Fraternity or sorority house', 'Home (Commuter)', 'Other'];
  
  // Time slots from 12 AM to 11 PM
  const timeSlots = [
    '12:00 AM', '1:00 AM', '2:00 AM', '3:00 AM', '4:00 AM', '5:00 AM',
    '6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM',
    '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM', '11:00 PM'
  ];

  const toggleTimeSlot = (time) => {
    if (!currentEditingDate) return;
    
    const dateKey = currentEditingDate.toDateString();
    setDateTimeMap(prev => {
      const currentTimes = prev[dateKey] || [];
      const newTimes = currentTimes.includes(time)
        ? currentTimes.filter(t => t !== time)
        : [...currentTimes, time];
      return { ...prev, [dateKey]: newTimes };
    });
  };

  const handleDateSelect = (dates) => {
    setSelectedDates(dates || []);
    // When a new date is selected, set it as the current editing date
    if (dates && dates.length > 0) {
      const latestDate = dates[dates.length - 1];
      setCurrentEditingDate(latestDate);
    } else {
      setCurrentEditingDate(null);
    }
  };

  const removeDate = (index) => {
    const dateToRemove = selectedDates[index];
    const dateKey = dateToRemove.toDateString();
    
    // Remove from selectedDates
    setSelectedDates(prev => prev.filter((_, i) => i !== index));
    
    // Remove times for this date
    setDateTimeMap(prev => {
      const newMap = { ...prev };
      delete newMap[dateKey];
      return newMap;
    });
    
    // If removing the currently editing date, switch to another
    if (currentEditingDate && currentEditingDate.toDateString() === dateKey) {
      const remainingDates = selectedDates.filter((_, i) => i !== index);
      setCurrentEditingDate(remainingDates.length > 0 ? remainingDates[0] : null);
    }
  };

  // Scroll behavior - restore scroll position if coming back from a school detail page
  useEffect(() => {
    if (location.state?.section === 'apply') {
      setActiveSection('apply');
      setTimeout(() => window.scrollTo(0, 0), 100);
      window.history.replaceState({}, document.title);
    } else if (location.state?.scrollPosition !== undefined) {
      // Restore the exact scroll position
      setTimeout(() => {
        window.scrollTo(0, location.state.scrollPosition);
      }, 100);
      // Clear the state so it doesn't persist
      window.history.replaceState({}, document.title);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.state]);

  // Handler for navigating to school detail - saves current scroll position
  const handleSchoolClick = (schoolId) => {
    const currentScrollPosition = window.scrollY;
    navigate(`/school/${schoolId}`, { state: { fromScrollPosition: currentScrollPosition } });
  };

  // Fetch schools from API
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        setLoadingSchools(true);
        const response = await fetch(`${API}/api/schools`);
        if (response.ok) {
          const data = await response.json();
          setSchools(data);
        }
      } catch (error) {
        console.error('Failed to fetch schools:', error);
      } finally {
        setLoadingSchools(false);
      }
    };
    fetchSchools();
  }, []);

  // Fetch approved guides
  useEffect(() => {
    const fetchApprovedGuides = async () => {
      try {
        const response = await fetch(`${API}/api/guides/approved`);
        if (response.ok) {
          const data = await response.json();
          setApprovedGuides(data);
        }
      } catch (error) {
        console.error('Failed to fetch guides:', error);
      }
    };
    fetchApprovedGuides();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Check total count (existing + new)
    if (selectedFiles.length + files.length > 5) {
      toast({
        title: "Too many photos",
        description: "You can upload a maximum of 5 photos",
        variant: "destructive"
      });
      return;
    }
    
    // Validate each file
    const validFiles = [];
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: `${file.name} is over 5MB and was skipped`,
          variant: "destructive"
        });
        continue;
      }
      validFiles.push(file);
    }
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removePhoto = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate photos before submitting
    if (!selectedFiles || selectedFiles.length < 2) {
      toast({
        title: "Photos Required",
        description: "Please upload at least 2 photos before submitting.",
        variant: "destructive"
      });
      alert("Please upload at least 2 photos before submitting. You currently have " + (selectedFiles ? selectedFiles.length : 0) + " photos.");
      return;
    }
    
    // Validate dates selected
    if (!selectedDates || selectedDates.length === 0) {
      toast({
        title: "Dates Required",
        description: "Please select at least one available date.",
        variant: "destructive"
      });
      alert("Please select at least one available date on the calendar.");
      return;
    }
    
    // Check if all dates have at least one time selected
    const datesWithoutTimes = selectedDates.filter(date => {
      const dateKey = date.toDateString();
      const times = dateTimeMap[dateKey] || [];
      return times.length === 0;
    });
    
    if (datesWithoutTimes.length > 0) {
      toast({
        title: "Times Required", 
        description: "Please select times for all selected dates.",
        variant: "destructive"
      });
      alert("Please select available times for each date you've chosen.");
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Create form data for file upload
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('school', formData.school);
      submitData.append('bio', formData.bio);
      // Format availability with dates and their specific times
      const availabilityParts = selectedDates
        .sort((a, b) => a - b)
        .map(date => {
          const dateKey = date.toDateString();
          const dateStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
          const times = dateTimeMap[dateKey] || [];
          return `${dateStr}: ${times.join(', ')}`;
        });
      const availabilityStr = availabilityParts.join(' | ');
      submitData.append('availability', availabilityStr);
      submitData.append('tour_description', formData.tourDescription);
      
      // New profile fields
      submitData.append('gender', formData.gender);
      submitData.append('academic_year', formData.academic_year);
      submitData.append('age', formData.age);
      submitData.append('admission_type', formData.admission_type);
      submitData.append('personality_type', formData.personality_type);
      submitData.append('college_rating', formData.college_rating);
      submitData.append('hometown', formData.hometown);
      submitData.append('major', formData.major);
      submitData.append('minor', formData.minor);
      submitData.append('extracurriculars', JSON.stringify(extracurriculars));
      submitData.append('clubs', formData.clubs);
      submitData.append('housing_experience', JSON.stringify(housingExperience));
      submitData.append('college_experience', formData.college_experience);
      submitData.append('tip_for_students', formData.tip_for_students);
      submitData.append('favorite_class', formData.favorite_class);
      submitData.append('career_goals', formData.career_goals);
      submitData.append('ideal_free_night', formData.ideal_free_night);
      submitData.append('high_school', formData.high_school);
      submitData.append('meeting_point', formData.meeting_point);
      
      // Append all photos - log each one
      console.log('Total photos to upload:', selectedFiles.length);
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        console.log(`Adding photo ${i + 1}: ${file.name}, size: ${file.size}, type: ${file.type}`);
        submitData.append('photos', file);
      }

      console.log('Submitting to:', `${API}/api/guides/apply`);
      
      const response = await fetch(`${API}/api/guides/apply`, {
        method: 'POST',
        body: submitData
      });

      const responseData = await response.json();
      console.log('Server response:', responseData);
      
      if (!response.ok) {
        console.error('Server error:', responseData);
        throw new Error(responseData.detail || 'Application submission failed');
      }

      console.log('Application submitted successfully:', responseData);
      
      alert("Success! Your application has been submitted. We'll review it and get back to you soon!");
      
      toast({
        title: "Application Submitted! 🎉",
        description: "Thank you for applying! We'll review your application and get back to you soon.",
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        school: '',
        bio: '',
        tourDescription: '',
        gender: '',
        academic_year: '',
        age: '',
        admission_type: '',
        personality_type: '',
        college_rating: '',
        hometown: '',
        major: '',
        minor: '',
        clubs: '',
        college_experience: '',
        tip_for_students: '',
        favorite_class: '',
        career_goals: '',
        ideal_free_night: '',
        high_school: '',
        meeting_point: ''
      });
      setSelectedFiles([]);
      setSelectedDates([]);
      setDateTimeMap({});
      setCurrentEditingDate(null);
      setExtracurriculars([]);
      setHousingExperience([]);
      setFormStep(1);
      setActiveSection('browse');

    } catch (error) {
      console.error('Application error:', error);
      toast({
        title: "Submission Failed",
        description: error.message || 'Failed to submit application. Please try again.',
        variant: "destructive",
        duration: 10000
      });
      // Also show alert for visibility
      alert('Error: ' + (error.message || 'Failed to submit application. Please check your photos and try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section 
        className="relative h-[50vh] bg-cover bg-center flex items-center justify-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://images.pexels.com/photos/1438072/pexels-photo-1438072.jpeg?auto=compress&cs=tinysrgb&w=1920')`
        }}
      >
        <div className="text-center text-white px-4 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-serif mb-4">Explore Campus Tours</h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto">
            Discover colleges across America through the eyes of current students. Book a campus tour or become a guide yourself!
          </p>
        </div>
      </section>

      {/* Toggle Buttons */}
      <div className="bg-white py-8 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center gap-4">
            <Button
              onClick={() => setActiveSection('browse')}
              className={`px-8 py-6 text-base transition-all duration-300 ${
                activeSection === 'browse'
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-black hover:bg-gray-200'
              }`}
            >
              <Building className="w-5 h-5 mr-2" />
              Browse Schools
            </Button>
            <Button
              onClick={() => setActiveSection('apply')}
              className={`px-8 py-6 text-base transition-all duration-300 ${
                activeSection === 'apply'
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-black hover:bg-gray-200'
              }`}
            >
              <GraduationCap className="w-5 h-5 mr-2" />
              Become a Guide
            </Button>
          </div>
        </div>
      </div>

      {/* Browse Schools Section */}
      {activeSection === 'browse' && (
        <section id="schools-grid" className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-serif mb-4">Popular Campus Tours</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Explore top colleges and universities with tours led by current students who know every corner of their campus.
              </p>
            </div>

            {/* Schools Grid */}
            <h3 className="text-2xl font-serif mb-6 text-center">Schools Available for Tours</h3>
            
            {loadingSchools ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : schools.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No schools available at the moment.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {schools.map((school) => (
                  <Card 
                    key={school.id} 
                    className="overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer group"
                    onClick={() => handleSchoolClick(school.id)}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={school.image} 
                        alt={school.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                        <div className="flex items-center text-white text-sm">
                          <MapPin className="w-4 h-4 mr-1" />
                          {school.city}
                        </div>
                      </div>
                    </div>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg leading-tight">{school.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <Button variant="outline" className="w-full text-sm">
                          View Campus Tours
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* CTA to become a guide */}
            <div className="mt-16 text-center bg-white rounded-lg p-8 shadow-md">
              <h3 className="text-2xl font-serif mb-4">Don't see your school?</h3>
              <p className="text-gray-600 mb-6">Be the first to offer campus tours at your college or university!</p>
              <Button 
                onClick={() => setActiveSection('apply')}
                className="bg-black text-white hover:bg-gray-800 px-8 py-6"
              >
                Apply to Be a Guide
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Become a Guide Section */}
      {activeSection === 'apply' && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-serif mb-4">Become a Campus Tour Guide</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Share your campus experience with prospective students and visitors. Earn money while showcasing the best of your school!
              </p>
            </div>

            {/* Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="text-center p-6 bg-white rounded-lg shadow-sm">
                <div className="text-3xl mb-3">💰</div>
                <h4 className="font-semibold mb-2">Earn Money</h4>
                <p className="text-sm text-gray-600">Set your own rates and schedule</p>
              </div>
              <div className="text-center p-6 bg-white rounded-lg shadow-sm">
                <div className="text-3xl mb-3">🎓</div>
                <h4 className="font-semibold mb-2">Build Skills</h4>
                <p className="text-sm text-gray-600">Develop leadership and communication</p>
              </div>
              <div className="text-center p-6 bg-white rounded-lg shadow-sm">
                <div className="text-3xl mb-3">🤝</div>
                <h4 className="font-semibold mb-2">Help Others</h4>
                <p className="text-sm text-gray-600">Guide future students to your school</p>
              </div>
            </div>

            {/* Application Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Guide Application</CardTitle>
                <CardDescription>
                  Fill out your profile to become a campus tour guide (Step {formStep} of 4)
                </CardDescription>
                {/* Progress Bar */}
                <div className="mt-4 flex gap-2">
                  {[1, 2, 3, 4].map(step => (
                    <div 
                      key={step}
                      className={`h-2 flex-1 rounded-full ${step <= formStep ? 'bg-black' : 'bg-gray-200'}`}
                    />
                  ))}
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  
                  {/* Step 1: Basic Info */}
                  {formStep === 1 && (
                    <>
                      <h3 className="font-semibold text-lg border-b pb-2">Basic Information</h3>
                      
                      {/* Name */}
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <div className="relative mt-1">
                          <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Your full name"
                            className="pl-10"
                          />
                        </div>
                      </div>

                      {/* Email */}
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <div className="relative mt-1">
                          <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="your.email@school.edu"
                            className="pl-10"
                          />
                        </div>
                      </div>

                      {/* Phone */}
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <div className="relative mt-1">
                          <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            placeholder="(555) 123-4567"
                            className="pl-10"
                          />
                        </div>
                      </div>

                      {/* School */}
                      <div>
                        <Label htmlFor="school">School/University *</Label>
                        <div className="relative mt-1">
                          <GraduationCap className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                          <Input
                            id="school"
                            name="school"
                            value={formData.school}
                            onChange={handleChange}
                            required
                            placeholder="e.g. Boston College"
                            className="pl-10"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {/* Gender */}
                        <div>
                          <Label>Gender</Label>
                          <select 
                            className="w-full border rounded-md p-2 mt-1"
                            value={formData.gender}
                            onChange={(e) => setFormData({...formData, gender: e.target.value})}
                          >
                            <option value="">Select...</option>
                            {genderOptions.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>

                        {/* Age */}
                        <div>
                          <Label>Age</Label>
                          <Input
                            type="number"
                            min="16"
                            max="50"
                            value={formData.age}
                            onChange={(e) => setFormData({...formData, age: e.target.value})}
                            placeholder="e.g. 21"
                            className="mt-1"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Academic Year */}
                        <div>
                          <Label>Academic Year *</Label>
                          <select 
                            className="w-full border rounded-md p-2 mt-1"
                            value={formData.academic_year}
                            onChange={(e) => setFormData({...formData, academic_year: e.target.value})}
                            required
                          >
                            <option value="">Select...</option>
                            {academicYearOptions.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>

                        {/* Admission Type */}
                        <div>
                          <Label>Admission Type</Label>
                          <select 
                            className="w-full border rounded-md p-2 mt-1"
                            value={formData.admission_type}
                            onChange={(e) => setFormData({...formData, admission_type: e.target.value})}
                          >
                            <option value="">Select...</option>
                            {admissionTypeOptions.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Personality Type */}
                        <div>
                          <Label>Personality Type</Label>
                          <select 
                            className="w-full border rounded-md p-2 mt-1"
                            value={formData.personality_type}
                            onChange={(e) => setFormData({...formData, personality_type: e.target.value})}
                          >
                            <option value="">Select...</option>
                            {personalityOptions.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>

                        {/* College Rating */}
                        <div>
                          <Label>College Experience Rating</Label>
                          <select 
                            className="w-full border rounded-md p-2 mt-1"
                            value={formData.college_rating}
                            onChange={(e) => setFormData({...formData, college_rating: e.target.value})}
                          >
                            <option value="">Select...</option>
                            {collegeRatingOptions.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <Button 
                        type="button" 
                        onClick={() => setFormStep(2)}
                        className="w-full bg-black text-white hover:bg-gray-800"
                        disabled={!formData.name || !formData.email || !formData.phone || !formData.school || !formData.academic_year}
                      >
                        Next: Academic Info →
                      </Button>
                    </>
                  )}

                  {/* Step 2: Academic Info */}
                  {formStep === 2 && (
                    <>
                      <h3 className="font-semibold text-lg border-b pb-2">Academic Information</h3>
                      
                      {/* Hometown */}
                      <div>
                        <Label>Hometown *</Label>
                        <Input
                          value={formData.hometown}
                          onChange={(e) => setFormData({...formData, hometown: e.target.value})}
                          placeholder="e.g. Arlington, MA"
                          required
                        />
                      </div>

                      {/* Major */}
                      <div>
                        <Label>Major(s) *</Label>
                        <Input
                          value={formData.major}
                          onChange={(e) => setFormData({...formData, major: e.target.value})}
                          placeholder="e.g. Economics"
                          required
                        />
                      </div>

                      {/* Minor */}
                      <div>
                        <Label>Minor(s)</Label>
                        <Input
                          value={formData.minor}
                          onChange={(e) => setFormData({...formData, minor: e.target.value})}
                          placeholder="e.g. Finance"
                        />
                      </div>

                      {/* High School */}
                      <div>
                        <Label>High School</Label>
                        <Input
                          value={formData.high_school}
                          onChange={(e) => setFormData({...formData, high_school: e.target.value})}
                          placeholder="e.g. Arlington High School"
                        />
                      </div>

                      {/* Extracurriculars */}
                      <div>
                        <Label>Extracurricular Activities</Label>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {extracurricularOptions.map(activity => (
                            <label key={activity} className="flex items-center gap-2 cursor-pointer text-sm">
                              <input
                                type="checkbox"
                                checked={extracurriculars.includes(activity)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setExtracurriculars([...extracurriculars, activity]);
                                  } else {
                                    setExtracurriculars(extracurriculars.filter(a => a !== activity));
                                  }
                                }}
                                className="accent-black"
                              />
                              {activity}
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Clubs */}
                      <div>
                        <Label>Clubs, Organizations & Involvement</Label>
                        <Textarea
                          value={formData.clubs}
                          onChange={(e) => setFormData({...formData, clubs: e.target.value})}
                          placeholder="e.g. Freshman League Club, Club Lacrosse, Ocean Conservation Club"
                          rows={2}
                        />
                      </div>

                      {/* Housing Experience */}
                      <div>
                        <Label>Housing Experience</Label>
                        <div className="mt-2 space-y-2">
                          {housingOptions.map(housing => (
                            <label key={housing} className="flex items-center gap-2 cursor-pointer text-sm">
                              <input
                                type="checkbox"
                                checked={housingExperience.includes(housing)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setHousingExperience([...housingExperience, housing]);
                                  } else {
                                    setHousingExperience(housingExperience.filter(h => h !== housing));
                                  }
                                }}
                                className="accent-black"
                              />
                              {housing}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-4">
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => setFormStep(1)}
                          className="flex-1"
                        >
                          ← Back
                        </Button>
                        <Button 
                          type="button" 
                          onClick={() => setFormStep(3)}
                          className="flex-1 bg-black text-white hover:bg-gray-800"
                          disabled={!formData.hometown || !formData.major}
                        >
                          Next: Personal Questions →
                        </Button>
                      </div>
                    </>
                  )}

                  {/* Step 3: Personal Questions */}
                  {formStep === 3 && (
                    <>
                      <h3 className="font-semibold text-lg border-b pb-2">Personal Questions</h3>
                      
                      {/* Bio */}
                      <div>
                        <Label>Bio / About You *</Label>
                        <Textarea
                          value={formData.bio}
                          onChange={(e) => setFormData({...formData, bio: e.target.value})}
                          required
                          rows={3}
                          placeholder="Hello! My name is ... and I am a ... at ... studying ..."
                        />
                      </div>

                      {/* College Experience */}
                      <div>
                        <Label>Describe your college experience</Label>
                        <Textarea
                          value={formData.college_experience}
                          onChange={(e) => setFormData({...formData, college_experience: e.target.value})}
                          rows={3}
                          placeholder="My college experience so far has been..."
                        />
                      </div>

                      {/* Tip for Students */}
                      <div>
                        <Label>Tip for future students</Label>
                        <Textarea
                          value={formData.tip_for_students}
                          onChange={(e) => setFormData({...formData, tip_for_students: e.target.value})}
                          rows={3}
                          placeholder="One great thing about this school is..."
                        />
                      </div>

                      {/* Favorite Class */}
                      <div>
                        <Label>Favorite class</Label>
                        <Textarea
                          value={formData.favorite_class}
                          onChange={(e) => setFormData({...formData, favorite_class: e.target.value})}
                          rows={2}
                          placeholder="My favorite class was... because..."
                        />
                      </div>

                      {/* Career Goals */}
                      <div>
                        <Label>Career goals</Label>
                        <Textarea
                          value={formData.career_goals}
                          onChange={(e) => setFormData({...formData, career_goals: e.target.value})}
                          rows={2}
                          placeholder="Currently, I am looking into..."
                        />
                      </div>

                      <div className="flex gap-4">
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => setFormStep(2)}
                          className="flex-1"
                        >
                          ← Back
                        </Button>
                        <Button 
                          type="button" 
                          onClick={() => setFormStep(4)}
                          className="flex-1 bg-black text-white hover:bg-gray-800"
                          disabled={!formData.bio}
                        >
                          Next: Tour Details →
                        </Button>
                      </div>
                    </>
                  )}

                  {/* Step 4: Tour Details & Photos */}
                  {formStep === 4 && (
                    <>
                      <h3 className="font-semibold text-lg border-b pb-2">Tour Details</h3>
                      
                      {/* Tour Description */}
                      <div>
                        <Label>Tour Description *</Label>
                        <Textarea
                          value={formData.tourDescription}
                          onChange={(e) => setFormData({...formData, tourDescription: e.target.value})}
                          required
                          rows={3}
                          placeholder="Describe the campus tour you would offer..."
                        />
                      </div>

                      {/* Meeting Point */}
                      <div>
                        <Label>Meeting Point *</Label>
                        <Input
                          value={formData.meeting_point}
                          onChange={(e) => setFormData({...formData, meeting_point: e.target.value})}
                          placeholder="e.g. In front of Saint Ignatius Church on campus"
                          required
                        />
                      </div>

                      {/* Photo Upload */}
                      <div>
                        <Label>Photos * <span className="text-gray-500 font-normal">(2-5 photos required)</span></Label>
                        <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                          <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                          <p className="text-gray-600 mb-2">Upload photos of yourself</p>
                          <p className="text-sm text-gray-500 mb-4">Include your face and photos on campus</p>
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleFileChange}
                            className="hidden"
                            id="photo-upload"
                          />
                          <label 
                            htmlFor="photo-upload"
                            className="cursor-pointer inline-block bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-md text-sm"
                          >
                            Choose Files
                          </label>
                        </div>
                        
                        {/* Photo Preview */}
                        {selectedFiles.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm font-medium mb-2">{selectedFiles.length} photo(s) selected:</p>
                            <div className="flex flex-wrap gap-2">
                              {selectedFiles.map((file, idx) => (
                                <div key={idx} className="relative group">
                                  <img 
                                    src={URL.createObjectURL(file)} 
                                    alt={`Preview ${idx + 1}`}
                                    className="w-20 h-20 object-cover rounded-lg"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removePhoto(idx)}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Availability Calendar */}
                      <div>
                        <Label>Availability * <span className="text-gray-500 font-normal">(Select dates and times)</span></Label>
                        <div className="mt-2 border rounded-lg p-4 bg-gray-50">
                          <div className="mb-4">
                            <p className="text-sm font-medium mb-2">Step 1: Select Available Dates</p>
                            <Calendar
                              mode="multiple"
                              selected={selectedDates}
                              onSelect={handleDateSelect}
                              disabled={(date) => date < new Date()}
                              className="mx-auto"
                            />
                          </div>
                          
                          {selectedDates && selectedDates.length > 0 && (
                            <div className="mb-4 border-t pt-4">
                              <p className="text-sm font-medium mb-2">Selected dates ({selectedDates.length}):</p>
                              <div className="flex flex-wrap gap-2">
                                {selectedDates
                                  .sort((a, b) => a - b)
                                  .map((date, index) => {
                                    const dateKey = date.toDateString();
                                    const times = dateTimeMap[dateKey] || [];
                                    const isEditing = currentEditingDate && currentEditingDate.toDateString() === dateKey;
                                    return (
                                      <button
                                        key={index}
                                        type="button"
                                        onClick={() => setCurrentEditingDate(date)}
                                        className={`inline-flex items-center text-xs px-3 py-2 rounded-lg border transition-colors ${
                                          isEditing 
                                            ? 'bg-black text-white border-black' 
                                            : times.length > 0 
                                              ? 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200'
                                              : 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200'
                                        }`}
                                      >
                                        <span>{date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                                        <span className="ml-2 text-xs opacity-75">
                                          {times.length > 0 ? `(${times.length} times)` : '(no times)'}
                                        </span>
                                        <span
                                          onClick={(e) => { e.stopPropagation(); removeDate(index); }}
                                          className="ml-2 hover:text-red-500 cursor-pointer"
                                        >
                                          <X className="w-3 h-3" />
                                        </span>
                                      </button>
                                    );
                                  })}
                              </div>
                              <p className="text-xs text-gray-500 mt-2">Click a date to select times for it</p>
                            </div>
                          )}
                          
                          {/* Time Slots for Selected Date */}
                          {currentEditingDate && (
                            <div className="border-t pt-4">
                              <p className="text-sm font-medium mb-3">
                                Step 2: Select Available Times for{' '}
                                <span className="text-black font-bold">
                                  {currentEditingDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                                </span>
                              </p>
                              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                                {timeSlots.map((time) => {
                                  const dateKey = currentEditingDate.toDateString();
                                  const currentTimes = dateTimeMap[dateKey] || [];
                                  const isSelected = currentTimes.includes(time);
                                  return (
                                    <button
                                      key={time}
                                      type="button"
                                      onClick={() => toggleTimeSlot(time)}
                                      className={`text-xs py-2 px-1 rounded-md border transition-colors ${
                                        isSelected
                                          ? 'bg-black text-white border-black'
                                          : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                                      }`}
                                    >
                                      {time}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600">
                          By submitting this application, you agree to our terms and conditions. 
                          Your application will be reviewed by our team.
                        </p>
                      </div>

                      <div className="flex gap-4">
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => setFormStep(3)}
                          className="flex-1"
                        >
                          ← Back
                        </Button>
                        <Button 
                          type="submit" 
                          className="flex-1 bg-black text-white hover:bg-gray-800"
                          disabled={isSubmitting || selectedFiles.length < 2 || !formData.tourDescription || !formData.meeting_point || selectedDates.length === 0}
                        >
                          {isSubmitting ? 'Submitting...' : 'Submit Application'}
                        </Button>
                      </div>
                    </>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default ExploreSchools;